import { useMemo } from 'react';
import { useSurveyQuestions } from '../../../../hooks/survey/useSurveyQuestions';
import type {
  ReactionStyle,
  SurveyQuestion,
} from '../../../../domain/Surveys/Questions/SurveyQuestion';
import { useSelectedQuestion } from './useSelectedQuestion';

type ViewMode =
  | 'wordcloud'
  | 'bar-chart-with-table'
  | 'net-promoter-score'
  | 'score-summary'
  | 'none';

type QuestionInfo =
  | { viewMode: Exclude<ViewMode, 'score-summary'>; uuid: string }
  | { viewMode: 'score-summary'; uuid: string; style: ReactionStyle };

function getQuestionInfo(question: SurveyQuestion): QuestionInfo {
  switch (question.type) {
    case 'single-open-ended-single-line':
    case 'single-open-ended-multiple-line':
      return { uuid: question.uuid, viewMode: 'wordcloud' };
    case 'multiple-close-ended':
    case 'single-close-ended':
    case 'rating-scale-5':
    case 'rating-scale-7':
      return { uuid: question.uuid, viewMode: 'bar-chart-with-table' };
    case 'net-promoter-score':
      return { uuid: question.uuid, viewMode: 'net-promoter-score' };
    case 'reaction':
      return { uuid: question.uuid, viewMode: 'score-summary', style: question.reaction_style };
    case 'email':
    default:
      return { uuid: question.uuid, viewMode: 'none' };
  }
}

export const useSelectedQuestionInfo = (
  surveyId: number,
  allowAll: boolean,
): 'ALL' | QuestionInfo | undefined => {
  const question = useSelectedQuestion(surveyId, allowAll);
  const { questions } = useSurveyQuestions(surveyId);

  const questionInfo = useMemo(() => {
    if (question === undefined) return undefined;

    if (question === 'ALL') {
      return 'ALL';
    }

    return getQuestionInfo(question);
  }, [question]);

  if (questions === undefined) return undefined;

  return questionInfo;
};
